import { useEffect, useState } from 'react'

import { auth } from '../services/firebase'
import { graphql } from '../services/graphql'

import { useStore, useStoreState } from './state'
import { useOnMount } from './use-on-mount'

export function useConnectionState() {
  const Store = useStore()

  const claims = useStoreState((store) => store.utils.session.claims)
  const active = useStoreState((store) => store.utils.session.active)
  const [user, setUser] = useState(auth().currentUser)

  const [loading, setLoading] = useState(true)
  const [isConnected, setIsConnected] = useState(false)

  // Vrai quand la session Firebase n'a pas pu s'ouvrir alors que le jeton API
  // est valide. Permet à l'écran de connexion d'expliquer, au lieu de
  // réafficher un formulaire vide.
  const [handshakeError, setHandshakeError] = useState(false)

  useOnMount(() => {
    return auth().onAuthStateChanged(setUser)
  })

  useEffect(() => {
    const checkSession = async () => {
      const updatedClaims = Store.getActions().utils.session.restore()

      // If a user is connected to firebase, but different from latest
      // token, sign them out
      if (user && user.uid !== updatedClaims._id) {
        await auth().signOut()
      }

      // If no user is connected to firebase, and there is a token,
      // connect them to firebase
      //
      // Maillon fragile de la connexion : le jeton API est acquis, la session
      // Firebase pas encore. La requête passe par le socket GraphQL, qui peut
      // ne pas être établi quand elle part. Sans traitement, l'échec ne
      // produisait AUCUN changement d'état — ni connecté, ni déconnecté — juste
      // un console.error et un retour au formulaire sans explication. D'où des
      // connexions qui n'aboutissent qu'à la troisième tentative.
      if (!user && updatedClaims._id) {
        try {
          const { administratorAuthenticated } = await graphql.query<{
            administratorAuthenticated: {
              firebaseToken: string
            } | null
          }>('{ administratorAuthenticated { firebaseToken } }')

          if (
            administratorAuthenticated &&
            administratorAuthenticated.firebaseToken
          ) {
            await auth().signInWithCustomToken(
              administratorAuthenticated.firebaseToken,
            )
            setHandshakeError(false)
          } else {
            // Jeton API accepté mais aucune session Firebase délivrée : il ne
            // vaut plus rien, on repart proprement du formulaire.
            Store.getActions().utils.session.disconnect()
            setIsConnected(false)
            setLoading(false)
          }
        } catch (error) {
          // Panne transitoire. On ne purge SURTOUT pas le jeton, qui est
          // valide : on signale, et une nouvelle tentative aboutira.
          console.error(error)
          setHandshakeError(true)
          setIsConnected(false)
          setLoading(false)
        }
      }

      // If no user is connected at all, then we are in disconnected state
      if (!user && !updatedClaims._id) {
        setIsConnected(false)
        setLoading(false)
      }

      // If a user is connected to firebase with latest token, then we are
      // in connected state
      if (user && updatedClaims._id === user.uid) {
        setIsConnected(true)
        setLoading(false)
      }
    }

    checkSession().catch(console.error)
  }, [Store, user, claims])

  useEffect(() => {
    // If the user is neither an admin nor a client account, disconnect them.
    // Client accounts (role === 'client') are allowed to stay connected even
    // though admin === false; their access is restricted in the router.
    //
    // Uniquement si le jeton lu est celui de l'utilisateur Firebase. La session
    // Firebase est partagée entre les onglets : un onglet resté sur l'écran de
    // connexion voit apparaître l'utilisateur qu'un autre onglet vient de
    // connecter, avec des claims encore vides (son jeton est relu juste après,
    // par `restore()`). Sans cette garde, il prenait ce compte pour un non-admin
    // et déconnectait tous les onglets.
    if (
      user &&
      claims._id === user.uid &&
      !claims.admin &&
      claims.role !== 'client'
    ) {
      Store.getActions().utils.session.disconnect()
    }
  }, [Store, user, claims.admin, claims.role, claims])

  useEffect(() => {
    // When a user connect, check if their account is active
    if (user && claims.admin) {
      graphql.queries.getAdministratorInfo().then(({ administrator }) => {
        Store.getActions().utils.session.setActive(administrator.active)
      })
    }
  }, [Store, user, claims.admin])

  return {
    active,
    loading,
    isConnected,
    handshakeError,
  }
}
